/**
 * "View query" affordance. Per the ticket, every chart exposes a small
 * button that reveals the underlying BigQuery SQL so the SQL can be
 * copy-pasted into the BQ console for ad-hoc drilldown.
 *
 * Renders a tiny button that toggles a `<details>` block with the SQL
 * and the parameter values. Keeps the chart compact; one click reveals
 * the full query. No portal / modal — admin only, no a11y headache.
 */
import { Code } from "lucide-react";
import { useState } from "react";
import type { QueryShape } from "./types";

interface ViewQueryButtonProps {
  query: QueryShape | undefined;
  label?: string;
}

export function ViewQueryButton({ query, label = "view query" }: ViewQueryButtonProps) {
  const [open, setOpen] = useState(false);
  if (!query) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(query.sql);
    } catch {
      // Clipboard API may be disabled; falling back to selecting the text would
      // require a portaled textarea — for an admin tool we accept the silent
      // no-op.
    }
  };

  return (
    <div className="text-xs">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-200"
      >
        <Code className="h-3 w-3" />
        {label}
      </button>
      {open && (
        <div className="mt-2 rounded border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-900 p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">
              BigQuery
            </span>
            <button
              type="button"
              onClick={copy}
              className="text-[10px] text-blue-600 dark:text-blue-400 hover:underline"
            >
              copy
            </button>
          </div>
          <pre className="overflow-x-auto whitespace-pre-wrap break-words text-[11px] font-mono text-zinc-700 dark:text-zinc-300">
            {query.sql}
          </pre>
          {query.params.length > 0 && (
            <div className="mt-2 border-t border-zinc-200 dark:border-zinc-700 pt-2">
              <span className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">
                Params
              </span>
              <ul className="mt-1 font-mono text-[11px] text-zinc-700 dark:text-zinc-300">
                {query.params.map((p) => (
                  <li key={p.name}>
                    <span className="text-zinc-500">@{p.name}</span> ({p.type}): {p.value}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
